import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from "@/components/ui/button"
import { Logo } from "@/components/ui/logo"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { VoiceWidget } from "@/components/voice-widget"
import {
  Phone,
  Loader2,
  Settings,
  Activity,
  PenLine,
  Telescope,
  Orbit,
  ChevronDown,
  AudioWaveform,
  BotMessageSquare,
  Chrome,
  Fingerprint,
  LogOut,
  Menu,
  X,
  MoreHorizontal,
  Crosshair,
  Settings as SettingsIcon,
  FileText,
} from "lucide-react"
import { ThemeSelector } from "@/components/theme-selector"
import { useChatActivity } from "@/components/providers/chat-activity-provider"
import { WritingPen } from "@/components/ui/writing-pen"
import { RadarSweep } from "@/components/ui/radar-sweep"
import { useRadarSweep } from "@/components/providers/radar-activity-provider"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/components/providers/auth-provider"
import { useNavigate, useLocation } from "react-router-dom"
import { useMutation, useQuery, useLazyQuery } from "@apollo/client"
import { useToast } from "@/hooks/use-toast"
import { INITIATE_VOICE_CALL, GET_INCOMING_CALL_NUMBER, GET_SIGNED_URL } from "@/lib/graphql/voice"
import { useConversation } from '@elevenlabs/react'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { SidebarResizer } from '@/components/sidebar/sidebar-resizer'
import { SidebarToggle } from '@/components/sidebar/sidebar-toggle'
import { SidebarTooltip } from '@/components/sidebar/sidebar-tooltip'
import { toggleSidebar, type SidebarLayout } from '@/lib/sidebar-size'
import { useDrawerMode, useFocusAfterFold, useSidebarLayout, useSidebarShortcut } from '@/hooks/use-sidebar-layout'
import '@/styles/sidebar.css'

/**
 * The DEMO tag under the logo (Bilanss WorkspaceTag). Assistive technology hears it once, as its phrase: the short
 * word on the plate stays out of the accessibility tree, where it could be spelt out letter by letter.
 */
function DemoTag() {
  return (
    <span className="workspace-tag">
      <span aria-hidden="true">Demo</span>
      <span className="sr-only">Demo workspace</span>
    </span>
  )
}

export function AppSidebar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userCallActive, setUserCallActive] = useState(false)
  const { user, logout, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const [fetchUserSignedUrl] = useLazyQuery(GET_SIGNED_URL, {
    fetchPolicy: 'network-only'
  })

  const [initiateCall, { loading: callLoading }] = useMutation(INITIATE_VOICE_CALL, {
    onCompleted: () => {
      toast({
        title: "Call Initiated Successfully!",
        description: "Your call is being connected. Check your phone in a moment.",
      })
    },
    onError: (error) => {
      toast({
        title: "Call Failed",
        description: error.message,
        variant: "destructive"
      })
    }
  })

  const tweepCred = useMemo(() => {
    const u = user as any
    const followers = u?.xFollowersCount || 0
    const following = u?.xFollowingCount || 1
    const tweets = u?.xTweetCount || 0
    const verified = u?.xVerified || false
    const accountAge = Math.floor((Date.now() - new Date(u?.xCreatedAt || Date.now()).getTime()) / (1000 * 60 * 60 * 24)) || 365
    const fi = Math.log(followers + 1) / 10
    const ri = Math.min(2, followers / following)
    const ai = Math.log(tweets + 1) / 15
    const gi = Math.log(accountAge + 1) / 8
    const vb = verified ? 1.5 : 1
    const pr = (fi * ri * ai * gi * vb) / 10000
    if (pr <= 1.0e-20) return 0
    return Math.min(100, Math.max(0, Math.round(130 + 5.21 * Math.log(pr))))
  }, [user])

  // Voice is off in the demo, and the API refuses this query for guests.
  const { data: incomingCallData } = useQuery(GET_INCOMING_CALL_NUMBER, { skip: !user || Boolean((user as any)?.isGuest) })
  const incomingCallNumber = incomingCallData?.getIncomingCallNumber || 'x11.social'

  // Initialize ElevenLabs conversation for voice widget
  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to ElevenLabs')
      toast({
        title: "Connected",
        description: "You're now connected to our AI assistant. Start speaking!",
      })
    },
    onDisconnect: () => {
      console.log('Disconnected from ElevenLabs')
      setUserCallActive(false)
      toast({
        title: "Call Ended",
        description: "Your voice call has ended.",
      })
    },
    onError: (error) => {
      console.error('ElevenLabs error:', error)
      setUserCallActive(false)
      toast({
        title: "Connection Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      })
    }
  })

  const handleCallNow = async () => {
    if (!user?.phoneVerified) {
      toast({
        title: "Phone Verification Required",
        description: "Please verify your phone number before making calls",
        variant: "destructive"
      })
      return
    }

    if (!user?.credits || user.credits < 1) {
      toast({
        title: "Insufficient Credits",
        description: "You need at least 1 minute of credits to make a call",
        variant: "destructive"
      })
      return
    }

    await initiateCall()
  }

  // Handle voice widget call (browser-based)
  const handleVoiceWidget = async () => {
    if (conversation.status === 'connected') {
      await conversation.endSession()
    }

    setUserCallActive(true)

    try {
      const { data } = await fetchUserSignedUrl()

      if (data?.getSignedUrl) {
        await conversation.startSession({
          signedUrl: data.getSignedUrl,
          dynamicVariables: {
            user_id: user?.id || ''
          }
        })
      } else {
        throw new Error('No signed URL received')
      }
    } catch (error: any) {
      console.error('Failed to start voice call:', error)
      setUserCallActive(false)

      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Please try again later.'
      const isInsufficientCredits = errorMessage.toLowerCase().includes('insufficient credits')

      toast({
        title: isInsufficientCredits ? "Insufficient Credits" : "Failed to Start Call",
        description: errorMessage,
        variant: "destructive"
      })
    }
  }

  // Handle ending the user call
  const handleEndUserCall = async () => {
    if (conversation.status === 'connected') {
      await conversation.endSession()
    }
    setUserCallActive(false)
  }

  const CHROME_EXTENSION_URL =
    'https://chromewebstore.google.com/detail/fnpbcidkcfbbijjnibgfhdablepmmefk'

  // The loop: create, Slop Cannon (/queue), drafts, learn — plus Angle, which the identity
  // strip asks you to feed from every screen and which had no name of its
  // own. The Chrome extension is an install link rather than a destination,
  // so it lives in the user menu instead of holding a permanent nav slot.
  // A reply keeps being written while the user is on another page; the
  // Create row says so until it lands. A radar sweep, asked for or on the
  // schedule, shows on the Slop Cannon row the same way.
  const { anyGenerating } = useChatActivity()
  const { sweeping } = useRadarSweep()
  const rowWork = (path: string) =>
    path === '/chat' && anyGenerating
      ? { icon: <WritingPen />, word: 'Writing', note: 'A reply is being written', name: 'Create, a reply is being written' }
      : path === '/queue' && sweeping
        ? { icon: <RadarSweep />, word: 'Finding', note: 'Finding stories', name: 'Slop Cannon, finding stories' }
        : null
  const navItems = [
    { path: '/chat', label: 'Create', icon: PenLine },
    { path: '/queue', label: 'Slop Cannon', icon: Crosshair },
    { path: '/drafts', label: 'Drafts', icon: FileText },
    { path: '/analytics', label: 'Analytics', icon: Activity },
    { path: '/inspiration', label: 'Inspiration', icon: Telescope },
    { path: '/circle', label: 'Circle', icon: Orbit },
    { path: '/angle', label: 'Angle', icon: Fingerprint },
    { path: '/settings', label: 'Settings', icon: SettingsIcon },
  ]

  const isActive = (path: string) => location.pathname === path
  const isGuest = Boolean((user as any)?.isGuest)
  const userName = user?.xDisplayName || user?.xUsername
  const userTier = user?.subscription?.tier || 'Free'
  // The user key's name, in both layouts: what its rail tooltip says, without the avatar's letter or the TweepCred bar.
  const userKeyName = userName ? `${userName} · ${userTier}` : undefined

  // The desktop sidebar, as in Bilanss: full or folded to an icon rail, as wide as the person dragged it, remembered
  // per browser. Below 768px the phone header and drawer take over and none of it applies. The sidebar is never
  // unmounted by folding, so a live voice session and the VoiceWidget below survive it.
  const drawer = useDrawerMode()
  const { layout, rail, width, viewportWidth, setLayout } = useSidebarLayout(drawer)
  const sidebarRef = useRef<HTMLElement>(null)
  const toggleKey = useRef<HTMLButtonElement>(null)
  const rememberFocus = useFocusAfterFold(sidebarRef, toggleKey, rail)

  /** Every way of folding (the key, the shortcut, a drag or a key on the edge) looks after focus the same way. */
  function changeLayout(next: SidebarLayout) {
    if (next.collapsed !== layout.collapsed) rememberFocus()
    setLayout(next)
  }
  function toggleRail() {
    changeLayout(toggleSidebar(layout))
  }
  useSidebarShortcut(!drawer, toggleRail)

  // The phone drawer belongs to phone widths: growing the window past them closes it, so its overlay does not stay
  // over the desktop page. Moving to another page closes it too.
  useEffect(() => {
    if (!drawer) setMobileMenuOpen(false)
  }, [drawer])
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // In the rail the menus open beside it rather than over it: 8px past the sidebar's edge, which is the rail's 12px
  // gutter past the key. A menu wider than the room left of its key (the voice menu in a 200px sidebar) is pushed
  // left by Radix; it stops 8px short of the window's edge rather than meeting it.
  const menuSide = rail ? 'right' : 'bottom'
  const menuOffset = rail ? 20 : 4
  const menuEdgeGap = 8

  return (
    <>
      <aside
        ref={sidebarRef}
        id="app-sidebar"
        aria-label="Sidebar"
        className={rail ? 'app-sidebar is-rail' : 'app-sidebar'}
      >
        <div className="sidebar-head">
          <div className="sidebar-brand">
            <button type="button" className="sidebar-logo" onClick={() => navigate(user ? '/chat' : '/')}>
              <Logo variant={rail ? 'rail' : 'sidebar'} />
            </button>
            {isGuest && <DemoTag />}
          </div>
          <SidebarToggle rail={rail} toggleKey={toggleKey} onToggle={toggleRail} />
        </div>

        <nav className="sidebar-nav" aria-label="Main">
          {navItems.map((item) => {
            const Icon = item.icon
            const work = rowWork(item.path)
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => navigate(item.path)}
                aria-current={isActive(item.path) ? 'page' : undefined}
                aria-busy={work ? true : undefined}
                // With the label hidden, the status word goes too; the name says it instead.
                aria-label={rail && work ? work.name : undefined}
                data-tip={rail ? item.label : undefined}
                data-tip-note={rail && work ? work.note : undefined}
                className="nav-row w-full"
              >
                {work ? work.icon : <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
                <span className="nav-row-label flex-1 text-left">{item.label}</span>
                {work && <span className="nav-row-status writing-glow text-xs">{work.word}</span>}
              </button>
            )
          })}
        </nav>

        {/* The person on a plate of their own, as in Bilanss */}
        <div className="sidebar-plate">
          {!isGuest && (
          <DropdownMenu>
            {rail ? (
              // The rail has room for one key: it opens the voice menu, whose first row starts the default call.
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={callLoading}
                  size="icon"
                  className="bg-foreground hover:bg-foreground/90 text-background"
                  aria-label="Voice Assistant"
                  data-tip="Voice Assistant"
                >
                  {callLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BotMessageSquare className="w-4 h-4" />}
                </Button>
              </DropdownMenuTrigger>
            ) : (
            <div className="flex items-center gap-0">
              <Button
                onClick={handleVoiceWidget}
                disabled={callLoading}
                className="sidebar-voice-key min-w-0 flex-1 justify-start bg-foreground hover:bg-foreground/90 text-background rounded-r-none px-3"
                title="Voice Assistant"
              >
                {callLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="truncate">Connecting...</span>
                  </>
                ) : (
                  <>
                    <BotMessageSquare className="w-4 h-4" />
                    <span className="truncate">Voice Assistant</span>
                  </>
                )}
              </Button>
              <DropdownMenuTrigger asChild>
                <Button
                  disabled={callLoading}
                  aria-label="Voice options"
                  className="sidebar-voice-more bg-foreground hover:bg-foreground/90 text-background rounded-l-none border-l border-background/20 px-2"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
            </div>
            )}
            <DropdownMenuContent side={menuSide} align="end" sideOffset={menuOffset} collisionPadding={menuEdgeGap} className="w-56">
              <DropdownMenuItem onClick={handleVoiceWidget}>
                <AudioWaveform className="w-4 h-4 mr-2" />
                Direct Convo
                <span className="ml-auto text-xs text-muted-foreground">Default</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCallNow}>
                <Phone className="w-4 h-4 mr-2" />
                Phone Call
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <span>Call to tweet:</span>
                  <span className="font-semibold text-foreground">{incomingCallNumber}</span>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          )}

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="sidebar-user w-full flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors motion-reduce:transition-none"
                aria-label={userKeyName}
                data-tip={rail ? userKeyName : undefined}
              >
                <Avatar className="w-7 h-7 flex-shrink-0">
                  <AvatarImage src={user?.xProfileImage} alt={user?.xUsername} />
                  <AvatarFallback className="bg-foreground text-background text-xs font-bold">
                    {(user?.xDisplayName || user?.xUsername || 'U').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="sidebar-user-text flex flex-col items-start flex-1 min-w-0 text-left">
                  <span className="text-xs font-semibold text-foreground leading-tight truncate w-full">
                    {userName}
                  </span>
                  <span className="text-[10px] text-muted-foreground leading-tight truncate w-full mt-0.5">
                    {userTier}
                  </span>
                  {tweepCred > 0 && (
                    <div className="w-full mt-0.5 flex items-center gap-1.5">
                      <span className="text-[8px] text-muted-foreground/60 uppercase tracking-wider shrink-0">TweepCred</span>
                      <div className="flex-1 bg-muted rounded-full h-1">
                        <div
                          className={`h-1 rounded-full transition-all motion-reduce:transition-none ${tweepCred >= 65 ? 'bg-emerald-500' : tweepCred >= 40 ? 'bg-amber-500' : 'bg-rose-500'}`}
                          style={{ width: `${tweepCred}%` }}
                        />
                      </div>
                      <span className="text-[9px] text-muted-foreground tabular-nums">{tweepCred}</span>
                    </div>
                  )}
                </div>
                <MoreHorizontal className="sidebar-user-more w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side={menuSide} align="end" sideOffset={menuOffset} collisionPadding={menuEdgeGap} className="w-48">
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <Settings className="w-4 h-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open(CHROME_EXTENSION_URL, '_blank', 'noopener,noreferrer')}
              >
                <Chrome className="w-4 h-4 mr-2" />
                Chrome Extension
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out @{user?.xUsername}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      {!drawer && (
        <SidebarResizer layout={layout} width={width} viewportWidth={viewportWidth} setLayout={changeLayout} />
      )}
      <SidebarTooltip sidebar={sidebarRef} resetKey={rail ? 'rail' : 'full'} />

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(user ? '/chat' : '/')}
              className="hover:opacity-75 transition-opacity"
            >
              <Logo variant="header" />
            </button>

            <div className="flex items-center gap-2">
              {!isGuest && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={callLoading}
                    size="icon"
                    className="bg-foreground hover:bg-foreground/90 text-background h-9 w-9"
                  >
                    {callLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <BotMessageSquare className="w-4 h-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={handleVoiceWidget}>
                    <AudioWaveform className="w-4 h-4 mr-2" />
                    Direct Convo
                    <span className="ml-auto text-xs text-muted-foreground">Default</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleCallNow}>
                    <Phone className="w-4 h-4 mr-2" />
                    Phone Call
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <span>Call to tweet:</span>
                      <span className="font-semibold text-foreground">{incomingCallNumber}</span>
                    </div>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              )}
              <Button
                onClick={() => navigate('/chat')}
                size="icon"
                variant="outline"
                className="relative hover:bg-accent h-9 w-9"
                aria-label={anyGenerating ? 'Create, a reply is being written' : 'Create'}
                aria-busy={anyGenerating || undefined}
              >
                {anyGenerating ? <WritingPen /> : <PenLine className="w-4 h-4" />}
              </Button>
              <ThemeSelector />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="h-10 w-10"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

        </div>
      </header>


      {/* Phone menu: a drawer over the page with a backdrop, as in Bilanss */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="flex w-[264px] flex-col gap-0 p-0 md:hidden motion-reduce:!animate-none">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="flex flex-col items-start gap-1 border-b border-border px-5 py-5">
            <Logo variant="header" />
            {isGuest && <DemoTag />}
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Main">
            <div className="flex flex-col gap-1.5">
              {navItems.map((item) => {
                const Icon = item.icon
                const work = rowWork(item.path)
                return (
                  <button
                    key={item.path}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    aria-busy={work ? true : undefined}
                    className="nav-row w-full"
                    onClick={() => {
                      navigate(item.path)
                      setMobileMenuOpen(false)
                    }}
                  >
                    {work ? work.icon : <Icon className="h-4 w-4 flex-shrink-0" />}
                    <span className="flex-1 text-left">{item.label}</span>
                    {work && <span className="writing-glow text-xs">{work.word}</span>}
                  </button>
                )
              })}
            </div>
            {!isGuest && (
              <div className="mt-4 flex flex-col gap-1.5 border-t border-border pt-4">
                <button className="nav-row w-full" onClick={() => { handleVoiceWidget(); setMobileMenuOpen(false) }}>
                  <AudioWaveform className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 text-left">Direct Convo</span>
                </button>
                <button className="nav-row w-full" onClick={() => { handleCallNow(); setMobileMenuOpen(false) }}>
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 text-left">Phone Call</span>
                </button>
                {incomingCallNumber && (
                  <p className="px-3 pt-1 text-xs text-muted-foreground">
                    Call to tweet: <span className="font-semibold text-foreground">{incomingCallNumber}</span>
                  </p>
                )}
              </div>
            )}
          </nav>
          <div className="border-t border-border bg-muted/40 px-4 py-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.xProfileImage} alt={user?.xUsername} />
                <AvatarFallback className="bg-foreground text-sm font-bold text-background">
                  {(user?.xDisplayName || user?.xUsername || 'U').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-semibold text-foreground">{user?.xDisplayName || user?.xUsername}</span>
                <span className="truncate text-xs text-muted-foreground">@{user?.xUsername}</span>
              </div>
            </div>
            <div className="mt-3 flex flex-col gap-1">
              {!isGuest && (
                <button className="nav-row w-full" onClick={() => { navigate('/profile'); setMobileMenuOpen(false) }}>
                  <Settings className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 text-left">Profile Settings</span>
                </button>
              )}
              <button className="nav-row w-full" onClick={() => { window.open(CHROME_EXTENSION_URL, '_blank', 'noopener,noreferrer'); setMobileMenuOpen(false) }}>
                <Chrome className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left">Chrome Extension</span>
              </button>
              <button className="nav-row w-full text-destructive hover:text-destructive" onClick={() => { logout(); setMobileMenuOpen(false) }}>
                <LogOut className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left">{isGuest ? 'Leave the demo' : 'Sign out'}</span>
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Voice Widget */}
      {isAuthenticated && (
        <VoiceWidget
          isActive={userCallActive && conversation.status === 'connected'}
          isSpeaking={conversation.isSpeaking}
          onStop={handleEndUserCall}
          onCancel={handleEndUserCall}
          isDemo={false}
          demoMinutesRemaining={user?.credits}
          getInputByteFrequencyData={conversation.getInputByteFrequencyData}
          getOutputByteFrequencyData={conversation.getOutputByteFrequencyData}
        />
      )}
    </>
  )
}
